import { Link, useParams } from "react-router-dom";
import { useFetch } from "../hooks/useFetch";
import { ErrorBanner } from "../components/ErrorBanner";
import { QuestionImages } from "../components/QuestionImages";
import { LoadingState, PageHeader, StatusBadge } from "../components/ui";
import type { StudentSubmissionDetail } from "../api/types";

interface DetailResponse {
  submission: StudentSubmissionDetail;
}

export function StudentResultDetailPage() {
  const params = useParams<{ submissionId: string }>();
  const submissionId = params.submissionId!;
  const { data, loading, error } = useFetch<DetailResponse>(
    `/student/submissions/${submissionId}`,
  );

  if (loading) return <LoadingState />;
  if (error) return <ErrorBanner error={error} />;
  if (!data) return <p>Not found.</p>;

  const s = data.submission;
  const isGraded = s.status === "graded";

  return (
    <section>
      <PageHeader
        title={s.test.title}
        subtitle={
          <span>
            <StatusBadge status={s.status} />
            {s.auto_submitted_at && (
              <em> &nbsp;auto-submitted when time expired</em>
            )}
          </span>
        }
        actions={<Link to="/student/results">← Back to my results</Link>}
      />
      <div className="card">
        <p style={{ margin: 0 }}>
          <strong>Submitted at:</strong> {s.submitted_at ?? "—"}
          {" · "}
          <strong>Graded at:</strong> {s.graded_at ?? "—"}
        </p>
        <p style={{ margin: "0.5rem 0 0" }}>
          <strong>Auto score:</strong> {s.total_auto_score ?? "—"}
          {" · "}
          <strong>Manual score:</strong> {s.total_manual_score ?? "—"}
          {" · "}
          <strong>Final score:</strong>{" "}
          <strong>{s.final_score ?? "—"}</strong> / {s.max_score}
        </p>
      </div>
      {isGraded && s.teacher_comment && (
        <blockquote className="free-text">
          <strong>Teacher comment:</strong> {s.teacher_comment}
        </blockquote>
      )}

      <h2>Questions</h2>
      <ol className="answers">
        {s.questions.map((q) => {
          const selected = new Set(q.answer?.selected_option_ids ?? []);
          return (
            <li key={q.id} className="answer">
              <p>
                <strong>{q.prompt}</strong>{" "}
                <small>
                  ({q.type}, {q.points} pts)
                </small>
              </p>
              <QuestionImages images={q.images} kind="student" />
              {q.type === "mcq" ? (
                <ul className="options">
                  {q.answer_options.map((o) => (
                    <li key={o.id}>
                      {selected.has(o.id) ? "[x]" : "[ ]"} {o.text}
                    </li>
                  ))}
                </ul>
              ) : (
                <blockquote className="free-text">
                  {q.answer?.text_answer ?? <em>No answer</em>}
                </blockquote>
              )}
              <p>
                <strong>Your score:</strong>{" "}
                {q.answer
                  ? `${q.answer.final_score} / ${q.points}`
                  : `0 / ${q.points}`}
              </p>
              {isGraded && q.answer?.teacher_feedback && (
                <blockquote className="free-text">
                  <strong>Feedback:</strong> {q.answer.teacher_feedback}
                </blockquote>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
